import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  LayoutDashboard, 
  FileText, 
  Settings, 
  Clock, 
  Calculator,
  LogOut,
  Building2,
  PanelLeftClose,
  PanelLeft,
  Search,
  Bell,
  HelpCircle,
  ChevronDown,
  Sparkles,
  Crown,
  Cog,
  Users
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/eba', icon: FileText, label: 'EBA Documents' },
  { path: '/rules', icon: Settings, label: 'Rules Engine' },
  { path: '/sites', icon: Building2, label: 'Sites' },
  { path: '/timesheets', icon: Clock, label: 'Timesheets' },
  { path: '/payroll', icon: Calculator, label: 'Payroll' },
  { path: '/employees', icon: Users, label: 'Employees' },
  { path: '/settings', icon: Cog, label: 'Settings' },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Get current page title
  const getCurrentPageTitle = () => {
    const currentNav = navItems.find(item => 
      item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
    );
    return currentNav?.label || 'Dashboard';
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-slate-50 flex">
        {/* Sidebar */}
        <aside 
          className={`
            ${isCollapsed ? 'w-[72px]' : 'w-[260px]'} 
            bg-equipr-black flex flex-col transition-all duration-300 ease-in-out flex-shrink-0 sidebar-nav
          `}
        >
          {/* Logo */}
          <div className={`h-16 flex items-center ${isCollapsed ? 'justify-center px-3' : 'px-5'} border-b border-white/10`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'gap-3'}`}>
              <div className="w-10 h-10 rounded-lg bg-equipr-yellow flex items-center justify-center flex-shrink-0 shadow-lg">
                <Calculator className="w-5 h-5 text-equipr-black" />
              </div>
              {!isCollapsed && (
                <span className="font-heading font-bold text-xl text-white tracking-tight">
                  EQUIPR
                </span>
              )}
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-3">
            <nav className={`${isCollapsed ? 'px-3' : 'px-4'} space-y-1`}>
              {navItems.map((item) => (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.path}
                      end={item.exact}
                      className={({ isActive }) =>
                        `nav-item flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-3'} py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${
                          isActive
                            ? 'active bg-white/15'
                            : 'hover:bg-white/10'
                        }`
                      }
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <div className={`flex items-center ${isCollapsed ? '' : 'gap-3'} ${isCollapsed ? 'w-10 h-10 justify-center' : 'w-full'}`}>
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                        {!isCollapsed && <span className="truncate">{item.label}</span>}
                      </div>
                    </NavLink>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right" sideOffset={10} className="bg-slate-900 text-white border-slate-700">
                      {item.label}
                    </TooltipContent>
                  )}
                </Tooltip>
              ))}
            </nav>
          </ScrollArea>

          {/* Bottom Section - Collapse Toggle & Version */}
          <div className={`${isCollapsed ? 'p-3' : 'p-4'} border-t border-white/10`}>
            {/* Version Display */}
            {!isCollapsed && (
              <p className="text-[10px] text-white/40 mb-3 text-center" data-testid="sidebar-version">
                {process.env.REACT_APP_VERSION || 'Development'}
              </p>
            )}
            
            {/* Collapse Toggle */}
            <div className={`${isCollapsed ? 'flex justify-center' : 'flex justify-end'}`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleSidebar}
                    className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                    data-testid="sidebar-toggle"
                  >
                    {isCollapsed ? (
                      <PanelLeft className="w-5 h-5" />
                    ) : (
                      <PanelLeftClose className="w-5 h-5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={10} className="bg-slate-900 text-white border-slate-700">
                  {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
          {/* Top Bar */}
          <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 flex-shrink-0">
            {/* Left: Search */}
            <div className="flex items-center gap-4 flex-1 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search..." 
                  className="pl-10 bg-slate-50 border-slate-200 focus:bg-white h-10"
                />
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Help */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-700">
                    <HelpCircle className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Help & Support</TooltipContent>
              </Tooltip>

              {/* Notifications */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-slate-500 hover:text-slate-700 relative">
                    <Bell className="w-5 h-5" />
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      3
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Notifications</TooltipContent>
              </Tooltip>

              {/* Divider */}
              <div className="w-px h-8 bg-slate-200 mx-2" />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-3 hover:bg-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-equipr-black flex items-center justify-center">
                      <span className="text-sm font-bold text-equipr-yellow">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="text-left hidden sm:block">
                      <p className="text-sm font-medium text-slate-700">{user?.client_id}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-medium">{user?.name}</span>
                      <span className="text-xs text-slate-500">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span>{user?.client_id}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/subscription')} className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-amber-500" />
                    <span>Subscription</span>
                    <Badge className="ml-auto bg-amber-100 text-amber-700 text-[10px] px-1.5">Enterprise</Badge>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-600">
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default Layout;
